import { useState, type FormEvent } from "react";
import { Link } from "@/lib/router";
import { ARTICLES, TRENDING_SLUGS } from "@/data/articles";
import { inr } from "@/lib/format";
import { articleHref } from "./Story";

export function Trending() {
  const items = TRENDING_SLUGS.map(s => ARTICLES.find(a => a.slug === s)).filter(a => a !== undefined);
  return (
    <section>
      <h2 className="splash border-b-[3px] border-ink pb-1 text-[1.6rem]">
        Most <span className="text-brand">Popular</span>
      </h2>
      <ol>
        {items.map((a, i) => (
          <li key={a.slug} className="border-b border-rule">
            <Link href={articleHref(a)} className="story flex gap-3 py-3">
              <span className="splash w-8 shrink-0 text-[2rem] leading-none text-brand">{i + 1}</span>
              <span className="headline text-[1rem]">{a.title}</span>
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function EmiCalculator() {
  const [amount, setAmount] = useState(5_000_000);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);

  const r = rate / 12 / 100;
  const n = years * 12;
  const emi = r === 0 ? amount / n : (amount * r * (1 + r) ** n) / ((1 + r) ** n - 1);
  const total = emi * n;

  const row = "flex items-baseline justify-between text-[13px] font-bold uppercase tracking-wide text-ink-soft";
  return (
    <section className="border-[3px] border-ink p-4">
      <h2 className="splash text-[1.5rem]">
        EMI <span className="text-brand">Calculator</span>
      </h2>
      <div className="mt-3 space-y-3">
        <label className="block">
          <span className={row}>
            Loan amount <span className="text-ink">{inr(amount)}</span>
          </span>
          <input type="range" min={500_000} max={50_000_000} step={100_000} value={amount} onChange={e => setAmount(+e.target.value)} className="w-full accent-[#c8102e]" />
        </label>
        <label className="block">
          <span className={row}>
            Interest rate <span className="text-ink">{rate.toFixed(2)}%</span>
          </span>
          <input type="range" min={6} max={14} step={0.05} value={rate} onChange={e => setRate(+e.target.value)} className="w-full accent-[#c8102e]" />
        </label>
        <label className="block">
          <span className={row}>
            Tenure <span className="text-ink">{years} yrs</span>
          </span>
          <input type="range" min={1} max={30} step={1} value={years} onChange={e => setYears(+e.target.value)} className="w-full accent-[#c8102e]" />
        </label>
      </div>
      <div className="mt-4 bg-wash p-3 text-center">
        <p className="text-[12px] font-bold uppercase tracking-wider text-muted">Monthly EMI</p>
        <p className="splash text-[2.2rem] text-brand">{inr(emi)}</p>
        <p className="text-[12px] font-semibold text-ink-soft">
          Total interest {inr(total - amount)} · Total paid {inr(total)}
        </p>
      </div>
    </section>
  );
}

export function Newsletter({ dark = false }: { dark?: boolean }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    // Hook this up to your email provider (Mailchimp, Brevo, etc.).
    setDone(true);
  };
  return (
    <section id="newsletter" className={dark ? "bg-ink p-6 text-white" : "border-t-[6px] border-brand bg-wash p-5"}>
      <h2 className="splash text-[1.8rem]">
        The <span className="text-brand">Brick</span> Briefing
      </h2>
      <p className={`mt-1 font-[family-name:var(--font-body)] text-[15px] ${dark ? "text-white/80" : "text-ink-soft"}`}>
        The day's biggest property stories, deals and rate moves, in your inbox every morning.
      </p>
      {done ? (
        <p className="mt-3 font-bold text-brand">You're in. Look out for tomorrow's briefing.</p>
      ) : (
        <form onSubmit={submit} className="mt-3 flex flex-col gap-2 sm:flex-row">
          <input
            type="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Your email address"
            className="min-w-0 flex-1 border-2 border-ink bg-white px-3 py-2 text-ink outline-none focus:border-brand"
          />
          <button className="bg-brand px-5 py-2 font-extrabold uppercase tracking-wide text-white hover:bg-brand-dark">Sign up</button>
        </form>
      )}
    </section>
  );
}
